import { breakdownPasswordDescription, PasswordRequirementsProps } from '@/utils/validators';
import cn from 'classnames';
import React, { useMemo } from 'react';
import { MdOutlineCheck, MdOutlineCircle } from 'react-icons/md';

type PasswordStrengthBarProps = {
    password: string;
    wrapperClassNames?: string;
    minComplexity?: number;
    currentComplexity?: number;
    passwordRequirement?: PasswordRequirementsProps;
};

const COLORS = [
    'bg-brand-red w-1', // 0 → Too Weak
    'bg-orange-500 w-1/4', // 1 → Weak
    'bg-yellow-500 w-2/4', // 2 → Fair
    'bg-brand-green w-3/4', // 3 → Good
    'bg-brand-green w-full' // 4 → Strong
];

const PasswordStrengthBar: React.FC<PasswordStrengthBarProps> = ({
    minComplexity = 2,
    currentComplexity = 0,
    password,
    passwordRequirement = {
        description: '',
        validationExpression: { value: /.*/, message: '' }
    }
}) => {
    const passwordRequirementDescriptionsWithStatus = useMemo(() => {
        if (!passwordRequirement?.description) return [];

        const descriptions = passwordRequirement.description
            .split(/\s*,\s*/)
            .map((item) => item.trim())
            .filter(Boolean);

        return breakdownPasswordDescription(descriptions, password, currentComplexity, minComplexity);
    }, [passwordRequirement, password]);

    return (
        <div
            className={cn(
                password
                    ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto h-max mt-2'
                    : 'opacity-0 -translate-y-2 scale-100 pointer-events-none h-0'
            )}>
            <div className="flex flex-col bg-brand-background-dark border border-brand-neutral-700 shadow-md text-brand-text rounded-md p-3 gap-2">
                <div className="flex flex-col gap-2">
                    <span className="text-xs">Password Strength: </span>

                    <div className="flex items-center gap-3">
                        {/* Strength bars */}
                        <div className="flex-1">
                            <div className="relative h-1 rounded bg-brand-neutral-700 overflow-hidden">
                                <div
                                    className={cn(
                                        'absolute left-0 top-0 h-full transition-all duration-300 ease-out',
                                        COLORS[currentComplexity]
                                    )}
                                />
                            </div>
                        </div>

                        {/* Label */}
                        {password && <p className="text-2xs text-brand-neutral-400">{currentComplexity} / 4</p>}
                    </div>
                </div>

                <div className="pt-2 border-t border-brand-neutral-700 flex flex-col gap-1">
                    {/* Password Requirement Description */}
                    {passwordRequirementDescriptionsWithStatus.map(({ description, fulfilled }, index) => (
                        <div key={index} className="flex flex-col text-xs gap-2">
                            <div className="flex items-center gap-2">
                                <div
                                    className={cn('rounded-full w-max p-0.5 transition-colors duration-200', {
                                        'text-brand-neutral-400 opacity-60': !fulfilled,
                                        'text-brand-neutral-800 border border-brand-primary-light bg-brand-primary-light':
                                            fulfilled
                                    })}>
                                    {fulfilled && <MdOutlineCheck />}
                                    {!fulfilled && <MdOutlineCircle />}
                                </div>

                                <span
                                    className={cn('transition-all duration-200', {
                                        'text-brand-neutral-400 opacity-60': !fulfilled,
                                        'text-brand-primary-light': fulfilled
                                    })}>
                                    {description}
                                </span>
                            </div>
                            {description.toLowerCase().includes('complexity') && (
                                <div className={cn('text-2xs ml-auto text-brand-neutral-400')}>
                                    To improve your password complexity, try using 10-12+ characters with uppercase,
                                    lowercase, numbers, and special characters.
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PasswordStrengthBar;
